<template>
  <div class="space-y-3">
    <div v-if="loading" class="flex justify-center py-6">
      <span class="pi pi-spin pi-spinner text-2xl text-primary" data-testid="result-amendment-loading"></span>
    </div>

    <div
      v-else-if="!amendments || amendments.length === 0"
      class="text-sm text-muted-color text-center py-6"
      data-testid="result-amendment-empty"
    >
      No corrections have been made to this result since it was published.
    </div>

    <ol v-else class="relative border-l border-surface-200 ml-3">
      <li
        v-for="entry in sortedAmendments"
        :key="entry.id"
        class="ml-4 pb-5"
        data-testid="result-amendment-entry"
      >
        <span
          class="absolute -left-2 flex items-center justify-center w-4 h-4 rounded-full ring-4 ring-surface-0 bg-primary"
        >
          <i class="pi pi-pencil text-[10px] text-surface-0"></i>
        </span>

        <div class="text-xs text-muted-color flex items-center gap-2">
          <span>{{ formatDate(entry.created_at) }}</span>
          <span v-if="entry.actor?.name">· {{ entry.actor.name }}</span>
        </div>

        <div class="mt-1 flex flex-wrap items-center gap-2 text-sm">
          <span class="font-medium text-color">{{ entry.assessment_name || 'Score' }}</span>
          <span class="text-muted-color" data-testid="result-amendment-old-score">{{ formatScore(entry.old_score) }}</span>
          <i class="pi pi-arrow-right text-muted-color text-xs"></i>
          <span class="font-semibold text-primary" data-testid="result-amendment-new-score">{{ formatScore(entry.new_score) }}</span>
        </div>

        <div class="mt-1 text-sm text-color">{{ entry.reason }}</div>
      </li>
    </ol>
  </div>
</template>

<script setup lang="ts">
import type { ResultAmendmentHistoryQuery } from '@/types/operations';

/**
 * Read-only timeline of post-publish score corrections for one result (#227),
 * mirroring `TranscriptActivityTimeline.vue`. Entries come from the signed,
 * immutable audit trail, so there is deliberately no edit or delete affordance.
 */
export type ResultAmendmentEntry = ResultAmendmentHistoryQuery['resultAmendmentHistory'][number];

const props = defineProps<{
  amendments: ReadonlyArray<ResultAmendmentEntry> | null | undefined;
  loading?: boolean;
}>();

// The server already orders newest-first; sorting here keeps the panel correct
// if it is ever fed from a cache or another source.
const sortedAmendments = computed(() =>
  [...(props.amendments ?? [])].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  ),
);

// A null old score means the assessment had no score before the correction.
const formatScore = (score: number | null | undefined): string =>
  score === null || score === undefined ? '—' : String(score);

const formatDate = (iso: string): string => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return String(iso);
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};
</script>
